interface DialogState {
  element: HTMLElement;
  origin: HTMLElement | null;
}

/** Keyboard/focus behavior for legacy-style conditional modal templates. */
export class ModalInteractionManager {
  private readonly dialogs = new Map<HTMLElement, DialogState>();
  private observer?: MutationObserver;
  private frame = 0;
  private restoreFrame = 0;
  private stopped = false;
  private lastFocus: HTMLElement | null = null;
  // These components already own their keyboard and focus lifecycle.
  private readonly owned = '[subscriptionDialogFocus], app-member-status-dialog, app-profile-editor-dialog';

  start(): void {
    this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(document.body, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style', 'hidden'] });
    document.addEventListener('pointerdown', this.pointerdown, true);
    document.addEventListener('keydown', this.busyGuard, true);
    document.addEventListener('click', this.busyGuard, true);
    document.addEventListener('keydown', this.keydown);
    document.addEventListener('focusin', this.focusin);
    document.addEventListener('click', this.click);
    this.sync();
  }

  stop(): void {
    this.stopped = true;
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.restoreFrame);
    document.removeEventListener('pointerdown', this.pointerdown, true);
    document.removeEventListener('keydown', this.busyGuard, true);
    document.removeEventListener('click', this.busyGuard, true);
    document.removeEventListener('keydown', this.keydown);
    document.removeEventListener('focusin', this.focusin);
    document.removeEventListener('click', this.click);
    this.dialogs.clear();
  }

  private visible(element: HTMLElement): boolean {
    return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
  }

  private openDialogs(): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>('.modal.show, .modal.d-block')).filter(el => this.visible(el));
  }

  private top(): HTMLElement | null {
    const element = this.openDialogs().at(-1);
    return element && !this.hasOwnLifecycle(element) ? element : null;
  }

  private hasOwnLifecycle(element: HTMLElement): boolean {
    // Mypage still uses Bootstrap for several persistent dialogs. Its existing
    // keyboard/focus lifecycle must not compete with this conditional-modal shim.
    const jquery = (window as Window & { jQuery?: (el: HTMLElement) => { data(key: string): unknown } }).jQuery;
    return !!element.closest(this.owned) || !!jquery?.(element).data('bs.modal');
  }

  private controls(element: HTMLElement): HTMLElement[] {
    return Array.from(element.querySelectorAll<HTMLElement>('a[href],button,input,select,textarea,[tabindex]'))
      .filter(el => el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[inert]') && this.visible(el));
  }

  private schedule(): void {
    if (this.frame || this.stopped) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
  }

  private sync(): void {
    const open = this.openDialogs();
    document.body.classList.toggle('modal-open', open.length > 0);
    let restore: HTMLElement | null = null;
    for (const [el, state] of this.dialogs) {
      if (!open.includes(el)) { this.dialogs.delete(el); restore = state.origin; }
    }
    for (const el of open) {
      if (this.hasOwnLifecycle(el) || this.dialogs.has(el)) continue;
      const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.dialogs.set(el, { element: el, origin: active && !el.contains(active) && active !== document.body ? active : this.lastFocus });
      if (!el.hasAttribute('tabindex')) el.tabIndex = -1;
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-hidden', 'false');
      if (!el.hasAttribute('role')) el.setAttribute('role', 'dialog');
      if (el === this.top()) el.focus();
    }
    if (restore?.isConnected && !open.some(el => el.contains(document.activeElement))) {
      const origin = restore;
      let attempts = 0;
      cancelAnimationFrame(this.restoreFrame);
      const returnFocus = () => {
        if (this.stopped || !origin.isConnected) return;
        const current = this.openDialogs().at(-1);
        if (current && !current.contains(origin)) return;
        if (origin.matches(':disabled') && attempts++ < 600) {
          this.restoreFrame = requestAnimationFrame(returnFocus);
        } else if (!origin.matches(':disabled')) origin.focus();
      };
      returnFocus();
    }
  }

  // Bootstrap's backdrop handler runs before the document bubble listener.
  // Honor the dialog's disabled-close contract before that handler can dismiss it.
  private readonly busyGuard = (event: Event): void => {
    const dialog = this.openDialogs().at(-1);
    if (!dialog) return;
    const close = dialog.querySelector<HTMLButtonElement>('.modal-header button.close, button.close, [data-dismiss="modal"], .modal-footer .bg-close');
    if (dialog.getAttribute('aria-busy') !== 'true' && !close?.matches(':disabled')) return;
    const escape = event instanceof KeyboardEvent && event.key === 'Escape';
    const background = event instanceof MouseEvent && event.target instanceof Element &&
      (event.target === dialog || event.target.classList.contains('modal-backdrop'));
    if (escape || background) { event.preventDefault(); event.stopImmediatePropagation(); }
  };

  private readonly pointerdown = (event: PointerEvent): void => {
    if (this.openDialogs().length || !(event.target instanceof Element)) return;
    const origin = event.target.closest<HTMLElement>('button,a[href],input,select,textarea,[tabindex]');
    if (origin && !origin.matches(':disabled')) this.lastFocus = origin;
  };

  private readonly focusin = (event: FocusEvent): void => {
    const dialog = this.top();
    if (dialog && event.target instanceof Node && !dialog.contains(event.target)) {
      dialog.focus();
    } else if (!this.openDialogs().length && event.target instanceof HTMLElement && event.target !== document.body) {
      this.lastFocus = event.target;
    }
  };

  private readonly keydown = (event: KeyboardEvent): void => {
    if (event.defaultPrevented) return;
    // Bootstrap 4 redirects focusin but does not wrap Tab at the browser-chrome
    // boundary. Supply only that boundary behavior; retain its open/close owner.
    const candidate = this.openDialogs().at(-1);
    const dialog = event.key === 'Tab' && candidate && !candidate.closest(this.owned)
      ? candidate : this.top();
    if (!dialog) return;
    if (event.key === 'Escape') {
      if (dialog.getAttribute('data-keyboard') === 'false' || dialog.getAttribute('aria-busy') === 'true') return;
      const close = dialog.querySelector<HTMLButtonElement>('.modal-header button.close, button.close, [data-dismiss="modal"], .modal-footer .bg-close');
      if (!close || close.matches(':disabled') || !this.visible(close)) return;
      event.preventDefault();
      close.click();
      return;
    }
    if (event.key !== 'Tab') return;
    const controls = this.controls(dialog);
    const index = controls.indexOf(document.activeElement as HTMLElement);
    if (!controls.length) { event.preventDefault(); dialog.focus(); return; }
    if (index < 0 || (event.shiftKey ? index === 0 : index === controls.length - 1)) {
      event.preventDefault();
      controls[event.shiftKey ? controls.length - 1 : 0].focus();
    }
  };

  private readonly click = (event: MouseEvent): void => {
    const dialog = this.top();
    if (!dialog || !(event.target instanceof Element)) return;
    const background = event.target === dialog || (event.target.classList.contains('modal-backdrop') && event.target.id !== 'side-nav-backdrop');
    if (!background || dialog.getAttribute('data-backdrop') === 'static' || dialog.getAttribute('aria-busy') === 'true') return;
    const close = dialog.querySelector<HTMLButtonElement>('.modal-header button.close, button.close, [data-dismiss="modal"], .modal-footer .bg-close');
    if (close && !close.matches(':disabled') && this.visible(close)) close.click();
  };
}
